import type { PortalNotificationFeedResponse } from '@/lib/api/client-portal.api'
import type { InfiniteData } from '@tanstack/react-query'
import { clientPortalApi } from '@/lib/api/client-portal.api'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

const portalNotificationRootKey = (entityId: string | undefined | null) => [
  'client-portal',
  entityId,
  'notifications',
]

const portalNotificationPopoverKey = (
  entityId: string | undefined | null,
  limit: number,
) => [...portalNotificationRootKey(entityId), 'popover', limit]

const portalNotificationHistoryKey = (entityId: string | undefined | null) => [
  ...portalNotificationRootKey(entityId),
  'history',
]

export function useClientPortalNotifications(
  entityId: string | undefined | null,
  limit = 20,
) {
  return useQuery({
    queryKey: portalNotificationPopoverKey(entityId, limit),
    queryFn: () => clientPortalApi.getNotificationFeed(entityId!, limit),
    enabled: !!entityId,
    staleTime: 15_000,
    gcTime: 30 * 60_000,
  })
}

export function useClientPortalNotificationHistory(
  entityId: string | undefined | null,
  pageSize = 30,
) {
  return useInfiniteQuery({
    queryKey: portalNotificationHistoryKey(entityId),
    queryFn: ({ pageParam }) =>
      clientPortalApi.getNotificationFeed(entityId!, pageSize, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + pageSize : undefined,
    enabled: !!entityId,
    staleTime: 15_000,
    gcTime: 30 * 60_000,
  })
}

function invalidatePortalNotificationCounts(
  queryClient: ReturnType<typeof useQueryClient>,
  entityId: string | undefined | null,
) {
  queryClient.invalidateQueries({
    queryKey: portalNotificationRootKey(entityId),
  })
  queryClient.invalidateQueries({
    queryKey: ['client-portal', entityId, 'sidebar-counts'],
  })
  queryClient.invalidateQueries({ queryKey: ['client-portal', 'my-entities'] })
}

export function useMarkPortalNotificationRead(
  entityId: string | undefined | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationId: string) =>
      clientPortalApi.markNotificationRead(notificationId),
    onMutate: notificationId => {
      queryClient.setQueriesData<PortalNotificationFeedResponse>(
        { queryKey: [...portalNotificationRootKey(entityId), 'popover'] },
        previous => {
          if (!previous) return previous
          const target = previous.items.find(item => item.id === notificationId)
          if (!target || target.readAt) return previous
          return {
            ...previous,
            unreadCount: Math.max(0, previous.unreadCount - 1),
            items: previous.items.map(item =>
              item.id === notificationId
                ? { ...item, readAt: new Date().toISOString() }
                : item,
            ),
          }
        },
      )
      queryClient.setQueryData<
        InfiniteData<PortalNotificationFeedResponse, number>
      >(portalNotificationHistoryKey(entityId), previous => {
        if (!previous) return previous
        return {
          ...previous,
          pages: previous.pages.map(page => ({
            ...page,
            unreadCount: Math.max(0, page.unreadCount - 1),
            items: page.items.map(item =>
              item.id === notificationId && !item.readAt
                ? { ...item, readAt: new Date().toISOString() }
                : item,
            ),
          })),
        }
      })
    },
    onSettled: () => invalidatePortalNotificationCounts(queryClient, entityId),
  })
}

export function useDismissPortalNotification(
  entityId: string | undefined | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationId: string) =>
      clientPortalApi.dismissNotification(notificationId),
    onMutate: notificationId => {
      queryClient.setQueriesData<PortalNotificationFeedResponse>(
        { queryKey: [...portalNotificationRootKey(entityId), 'popover'] },
        previous => {
          if (!previous) return previous
          const target = previous.items.find(item => item.id === notificationId)
          if (!target) return previous
          return {
            ...previous,
            items: previous.items.filter(item => item.id !== notificationId),
            unreadCount: target.readAt
              ? previous.unreadCount
              : Math.max(0, previous.unreadCount - 1),
          }
        },
      )
      queryClient.setQueryData<
        InfiniteData<PortalNotificationFeedResponse, number>
      >(portalNotificationHistoryKey(entityId), previous => {
        if (!previous) return previous
        const target = previous.pages
          .flatMap(page => page.items)
          .find(item => item.id === notificationId)
        if (!target) return previous
        return {
          ...previous,
          pages: previous.pages.map(page => ({
            ...page,
            items: page.items.filter(item => item.id !== notificationId),
            unreadCount: target.readAt
              ? page.unreadCount
              : Math.max(0, page.unreadCount - 1),
            totalCount: Math.max(0, page.totalCount - 1),
          })),
        }
      })
    },
    onSettled: () => invalidatePortalNotificationCounts(queryClient, entityId),
  })
}

export function useMarkAllPortalNotificationsRead(
  entityId: string | undefined | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => clientPortalApi.markAllNotificationsRead(entityId!),
    onMutate: () => {
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<PortalNotificationFeedResponse>(
        { queryKey: [...portalNotificationRootKey(entityId), 'popover'] },
        previous =>
          previous
            ? {
                ...previous,
                unreadCount: 0,
                items: previous.items.map(item => ({
                  ...item,
                  readAt: item.readAt ?? readAt,
                })),
              }
            : previous,
      )
      queryClient.setQueryData<
        InfiniteData<PortalNotificationFeedResponse, number>
      >(portalNotificationHistoryKey(entityId), previous =>
        previous
          ? {
              ...previous,
              pages: previous.pages.map(page => ({
                ...page,
                unreadCount: 0,
                items: page.items.map(item => ({
                  ...item,
                  readAt: item.readAt ?? readAt,
                })),
              })),
            }
          : previous,
      )
    },
    onSettled: () => invalidatePortalNotificationCounts(queryClient, entityId),
  })
}
